import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { ChatRegular } from "@fluentui/react-icons";
import type { KommentarFaden, KommentarRect, KommentarStelle } from "../../../services/dokumentKommentarService";
import { textRechteckeDerSelection } from "./auswahlText";
import { inhaltsBox } from "./EntfernenEbene";
import { KommentarBlase } from "./KommentarBlase";
import { hatStelle, originalSeite } from "./kommentarZuordnung";
import { auswahlZuRechtecken, cssZuPdfRect, pdfZuCssRect, type ViewportLike } from "./pdfKoordinaten";
import type { SeitenGeometrie } from "./seitenGeometrie";
import { REFINED } from "../../risikoanalyseStep/tokens";
import type { KommentarSteuerung } from "./useDokumentKommentare";
import { alleDokumente, alleFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Kommentare an Stellen des PDFs (User, 28.09.2026: „Element, Text kann man
 * kommentieren, wird markiert und ein Chat-Icon angezeigt (minimiert)“).
 *
 * Läuft im Lese-Viewer (react-pdf) UND im Editor (PDF.js) — beide reichen eine
 * {@link SeitenGeometrie}. Die Kommentare liegen in KMVS, nicht in der Datei.
 *
 * - Je Faden: gelbe Markierung über der Stelle (erledigt: grau, gestrichelt)
 *   und an ihrer rechten oberen Ecke ein minimiertes Chat-Symbol.
 * - Klick aufs Symbol → Blase daneben (kein modaler Dialog). Klick daneben
 *   oder Esc minimiert wieder.
 * - Werkzeug „Kommentar“: Text auswählen, Bereich aufziehen oder auf eine
 *   Stelle klicken (→ 16×16-pt-Punkt). Die Markierung steht sofort, die Blase
 *   öffnet mit leerem Kasten; der erste Enter speichert. Leer lassen + Klick
 *   daneben verwirft.
 *
 * Die Markierungen hängen per Portal in den Seiten-Elementen — PDF.js leert
 * seine `.page`-Divs beim Zoomen, deshalb wird der Host nach jedem Render
 * wieder eingehängt (wie in `EntfernenEbene`).
 */

/** Kleiner als das (CSS-px) gilt als Klick auf eine Stelle, nicht als Bereich. */
const MIN_ZUG = 4;
/** Halbe Kante des Punkts bei einem Klick — 16×16 pt. */
const PUNKT_HALB = 8;
const SYMBOL = 22;
/** Symbole stehen im rechten Seitenrand (wie Acrobat): so weit von der rechten Kante. */
const RAND_ABSTAND = 30;
/** Mindestabstand (oben zu oben) gestapelter Symbole auf gleicher Höhe. */
const STAPEL_ABSTAND = 26;
const GRAU = "#8a8a8a";
const BLASE_BREITE = 300;

/** Neuer, noch nicht gespeicherter Kommentar. `minimiert`: nur Markierung + Symbol. */
type Entwurf = KommentarStelle & { angezeigt: number; minimiert: boolean };

const ENTWURF_OFFEN = "Kommentar-Entwurf offen — erst speichern (Enter) oder seinen Text löschen.";
type Zug = { seite: number; x0: number; y0: number; x1: number; y1: number };

const stil = {
    markierung: {
        position: "absolute",
        boxSizing: "border-box",
        pointerEvents: "none",
        background: "rgba(255, 213, 0, 0.32)",
        borderBottom: "2px solid rgba(230, 170, 0, 0.9)",
        mixBlendMode: "multiply",
    } as React.CSSProperties,
    markierungErledigt: {
        background: "rgba(140, 140, 140, 0.12)",
        border: "1px dashed rgba(110, 110, 110, 0.8)",
        mixBlendMode: "normal",
    } as React.CSSProperties,
    markierungOffen: { outline: "2px solid rgba(125, 33, 105, 0.8)", outlineOffset: 1 } as React.CSSProperties,
    // Zeigt, welches Symbol zu welcher Stelle gehört.
    markierungHover: { outline: `3px solid ${REFINED.aub}`, outlineOffset: 1 } as React.CSSProperties,
    // Minimiertes Chat-Symbol: gefüllter Kreis in der Markenfarbe (Aubergine,
    // REFINED-Token), weißes Symbol, leichter Schatten — auf jedem PDF-Hintergrund sichtbar.
    symbol: {
        position: "absolute",
        width: SYMBOL,
        height: SYMBOL,
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        border: "1.5px solid #fff",
        boxSizing: "border-box",
        background: REFINED.aub,
        color: "#fff",
        cursor: "pointer",
        pointerEvents: "auto",
        boxShadow: "0 1px 4px rgba(40, 10, 35, 0.35)",
        fontSize: 14,
    } as React.CSSProperties,
    symbolErledigt: { background: GRAU } as React.CSSProperties,
    symbolHover: { transform: "scale(1.15)", boxShadow: "0 2px 8px rgba(40, 10, 35, 0.55)" } as React.CSSProperties,
    // Entwurf: noch nicht gespeichert — hell mit gestricheltem Rand.
    symbolEntwurf: { background: "#fff", color: REFINED.aub, border: `1.5px dashed ${REFINED.aub}` } as React.CSSProperties,
    zaehler: {
        position: "absolute",
        top: -6,
        right: -7,
        minWidth: 14,
        height: 14,
        padding: "0 3px",
        borderRadius: 7,
        background: "#fff",
        color: REFINED.aub,
        border: `1px solid ${REFINED.aub}`,
        fontSize: 9,
        fontWeight: 700,
        lineHeight: "12px",
        textAlign: "center",
        boxSizing: "border-box",
    } as React.CSSProperties,
    zug: {
        position: "absolute",
        border: "1px dashed rgba(200, 150, 0, 1)",
        background: "rgba(255, 213, 0, 0.18)",
        pointerEvents: "none",
    } as React.CSSProperties,
};

const vereinige = (rs: KommentarRect[]): KommentarRect => [
    Math.min(...rs.map((r) => r[0])),
    Math.min(...rs.map((r) => r[1])),
    Math.max(...rs.map((r) => r[2])),
    Math.max(...rs.map((r) => r[3])),
];

const auswahlIn = (container: HTMLElement | null): Selection | null => {
    const sel = window.getSelection?.();
    if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const drin = (n: Node | null) => !!n && container.contains(n);
    return drin(sel.anchorNode) || drin(sel.focusNode) ? sel : null;
};

export const KommentarEbene: React.FC<{
    geometrie: SeitenGeometrie | null;
    /** Scrollender Container des Viewers — hier beginnt das Setzen. */
    container: HTMLElement | null;
    kommentare: KommentarSteuerung;
    /** Werkzeug „Kommentar“ gewählt? */
    werkzeugAktiv: boolean;
    /** Schreibrecht am Dokument: kommentieren, antworten, erledigt. */
    darfSchreiben: boolean;
    /** Ein Entwurf steht — der Aufrufer schaltet das Werkzeug ab. */
    onEntwurf?: () => void;
    /**
     * Editor nach „Seiten übernehmen“ (noch nicht gespeichert): gespeicherte
     * Seite i steht jetzt an Position `seitenZuordnung[i]` (-1 = gelöscht).
     */
    seitenZuordnung?: number[] | null;
    onHinweis?: (text: string | null) => void;
}> = ({ geometrie, container, kommentare, werkzeugAktiv, darfSchreiben, onEntwurf, seitenZuordnung = null, onHinweis }) => {
    const [, neuZeichnen] = React.useReducer((n: number) => n + 1, 0);
    const [entwurf, setEntwurf] = React.useState<Entwurf | null>(null);
    // Aktueller Entwurf für Ereignis-Handler, die nicht bei jeder Änderung neu gebunden werden.
    const entwurfRef = React.useRef<Entwurf | null>(null);
    entwurfRef.current = entwurf;
    const [zug, setZug] = React.useState<Zug | null>(null);
    // Zusammengehörigkeit sichtbar machen: Hover über Symbol bzw. über Markierung.
    const [hoverSymbol, setHoverSymbol] = React.useState<string | null>(null);
    const [hoverMarkierung, setHoverMarkierung] = React.useState<string | null>(null);
    // Markierungen des letzten Renders (CSS relativ zur Seite) — für den Hover-Treffer.
    const treffer = React.useRef<{ seite: number; id: string; box: { left: number; top: number; width: number; height: number } }[]>([]);
    const hosts = React.useRef(new Map<number, HTMLDivElement>());
    const benutzteSeiten = React.useRef(new Set<number>());
    // Ungesendeter Text je Faden (und für den Entwurf) — überlebt das Minimieren.
    const entwuerfe = React.useRef(new Map<string, { current: string }>());
    const textRef = (schluessel: string) => {
        let r = entwuerfe.current.get(schluessel);
        if (!r) {
            r = { current: "" };
            entwuerfe.current.set(schluessel, r);
        }
        return r as React.MutableRefObject<string>;
    };

    const { faeden, sichtbar, offen, setOffen, setSeitenzahl } = kommentare;

    // Seitenzahl der GESPEICHERTEN Fassung melden (nach ungespeichertem Umbau: Länge
    // der Zuordnung). Kommentare jenseits davon stehen „ohne Stelle“.
    const gespeicherteSeiten = seitenZuordnung ? seitenZuordnung.length : (geometrie?.anzahl() ?? 0);
    React.useEffect(() => {
        setSeitenzahl(gespeicherteSeiten > 0 ? gespeicherteSeiten : null);
    }, [gespeicherteSeiten, setSeitenzahl]);
    // Beim Neuladen (andere Fassung) ist die Seitenzahl wieder unbekannt.
    React.useEffect(() => () => setSeitenzahl(null), [setSeitenzahl]);

    React.useEffect(() => (geometrie ? geometrie.onAenderung(() => neuZeichnen()) : undefined), [geometrie]);

    React.useEffect(() => {
        const map = hosts.current;
        return () => {
            for (const h of map.values()) h.remove();
            map.clear();
        };
    }, []);

    const hostFuer = (i: number): HTMLDivElement => {
        let host = hosts.current.get(i);
        if (!host) {
            host = document.createElement("div");
            host.className = "kmvs-kommentar-ebene";
            Object.assign(host.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "30" });
            hosts.current.set(i, host);
        }
        return host;
    };

    React.useLayoutEffect(() => {
        for (const [i, host] of hosts.current) {
            const el = benutzteSeiten.current.has(i) ? geometrie?.seitenElement(i) : null;
            if (el && host.parentNode !== el) el.appendChild(host);
            else if (!el && host.parentNode) host.remove();
        }
    });

    /** Angezeigte Position einer gespeicherten Seite (Editor mit ungespeichertem Umbau). */
    const angezeigt = React.useCallback(
        (seite: number | null): number | null => {
            if (seite === null) return null;
            if (!seitenZuordnung) return seite;
            const p = seitenZuordnung[seite];
            return p === undefined || p === -1 ? null : p;
        },
        [seitenZuordnung],
    );

    const seiteVon = React.useCallback(
        (ziel: Node): number | null => {
            if (!geometrie) return null;
            for (let i = 0; i < geometrie.anzahl(); i++) {
                if (geometrie.seitenElement(i)?.contains(ziel)) return i;
            }
            return null;
        },
        [geometrie],
    );

    const entwurfHatText = () => !!textRef("entwurf").current.trim();

    /** Entwurf zuklappen: mit Text nur minimieren, leer verwerfen. */
    const entwurfZuklappen = React.useCallback(() => {
        setEntwurf((e) => (!e ? null : textRef("entwurf").current.trim() ? { ...e, minimiert: true } : null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const entwurfSetzen = React.useCallback(
        (angez: number, rect: KommentarRect, zitat: string) => {
            const seite = originalSeite(seitenZuordnung, angez);
            if (seite === null) {
                onHinweis?.("Eingefügte Seiten lassen sich erst nach dem Speichern kommentieren.");
                return;
            }
            // Getippten Text nie still verwerfen: der offene Entwurf gewinnt.
            if (entwurfRef.current && entwurfHatText()) {
                onHinweis?.(ENTWURF_OFFEN);
                setEntwurf({ ...entwurfRef.current, minimiert: false });
                setOffen(null);
                onEntwurf?.();
                return;
            }
            textRef("entwurf").current = "";
            setEntwurf({ seite, rect, zitat: zitat.slice(0, 500), angezeigt: angez, minimiert: false });
            setOffen(null);
            onEntwurf?.();
        },
        // textRef ist stabil (Ref-Map).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onEntwurf, onHinweis, seitenZuordnung, setOffen],
    );

    const entwurfAusAuswahl = React.useCallback(
        (sel: Selection): boolean => {
            if (!geometrie) return false;
            const kaesten = textRechteckeDerSelection(sel);
            // Der Anker liegt auf der Seite des ersten Auswahlstücks.
            for (let i = 0; i < geometrie.anzahl(); i++) {
                const el = geometrie.seitenElement(i);
                const v = geometrie.viewport(i);
                if (!el || !v) continue;
                const box = inhaltsBox(el);
                const hier = kaesten.filter((r) => {
                    const mx = r.left + r.width / 2;
                    const my = r.top + r.height / 2;
                    return mx >= box.left && mx <= box.right && my >= box.top && my <= box.bottom;
                });
                const rects = auswahlZuRechtecken(hier, box, v);
                if (!rects.length) continue;
                entwurfSetzen(i, vereinige(rects), sel.toString().replace(/\s+/g, " ").trim());
                sel.removeAllRanges();
                return true;
            }
            return false;
        },
        [entwurfSetzen, geometrie],
    );

    // Werkzeug an, während schon Text ausgewählt ist → sofort daraus.
    React.useEffect(() => {
        if (!werkzeugAktiv) return;
        const sel = auswahlIn(container);
        if (sel) entwurfAusAuswahl(sel);
        // Nur beim Einschalten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [werkzeugAktiv]);

    React.useEffect(() => {
        if (!container) return;
        const vorher = container.style.cursor;
        if (werkzeugAktiv) container.style.cursor = "crosshair";
        return () => {
            container.style.cursor = vorher;
        };
    }, [werkzeugAktiv, container]);

    // Setzen: Klick, Bereich oder Textauswahl.
    React.useEffect(() => {
        if (!container || !werkzeugAktiv || !geometrie) return;
        let beenden: (() => void) | null = null;
        const onDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            beenden?.();
            const ziel = e.target as HTMLElement | null;
            if (!ziel || typeof ziel.closest !== "function") return;
            if (ziel.closest(".kmvs-kommentar-ebene")) return;
            const seite = seiteVon(ziel);
            if (seite === null) return;
            const el = geometrie.seitenElement(seite);
            const v = geometrie.viewport(seite);
            if (!el || !v) return;
            const aufText = !!ziel.closest(".textLayer span") && !ziel.classList.contains("endOfContent");
            if (aufText && !e.altKey) {
                // Über Text wählt man Text aus — ausgewertet beim Loslassen.
                const onUp = () => {
                    alleFenster.removeEventListener("pointerup", onUp);
                    beenden = null;
                    const sel = auswahlIn(container);
                    if (sel) entwurfAusAuswahl(sel);
                };
                alleFenster.addEventListener("pointerup", onUp);
                beenden = () => alleFenster.removeEventListener("pointerup", onUp);
                return;
            }
            e.preventDefault();
            const box = inhaltsBox(el);
            const start = { x: e.clientX - box.left, y: e.clientY - box.top };
            let aktuell: Zug = { seite, x0: start.x, y0: start.y, x1: start.x, y1: start.y };
            const klemme = (w: number, max: number) => Math.min(Math.max(w, 0), max);
            const onMove = (ev: PointerEvent) => {
                aktuell = { ...aktuell, x1: klemme(ev.clientX - box.left, box.width), y1: klemme(ev.clientY - box.top, box.height) };
                setZug(aktuell);
            };
            const abbauen = () => {
                alleFenster.removeEventListener("pointermove", onMove);
                alleFenster.removeEventListener("pointerup", onUp);
                alleFenster.removeEventListener("pointercancel", abbauen);
                beenden = null;
                setZug(null);
            };
            const onUp = (ev: PointerEvent) => {
                onMove(ev);
                abbauen();
                const vp: ViewportLike | null = geometrie.viewport(seite);
                if (!vp) return;
                if (Math.abs(aktuell.x1 - aktuell.x0) < MIN_ZUG && Math.abs(aktuell.y1 - aktuell.y0) < MIN_ZUG) {
                    const [px, py] = vp.convertToPdfPoint(aktuell.x0, aktuell.y0);
                    const r = (n: number) => Math.round(n * 100) / 100;
                    entwurfSetzen(seite, [r(px - PUNKT_HALB), r(py - PUNKT_HALB), r(px + PUNKT_HALB), r(py + PUNKT_HALB)], "");
                    return;
                }
                entwurfSetzen(seite, cssZuPdfRect(vp, aktuell.x0, aktuell.y0, aktuell.x1, aktuell.y1), "");
            };
            alleFenster.addEventListener("pointermove", onMove);
            alleFenster.addEventListener("pointerup", onUp);
            alleFenster.addEventListener("pointercancel", abbauen);
            beenden = abbauen;
        };
        container.addEventListener("pointerdown", onDown);
        return () => {
            container.removeEventListener("pointerdown", onDown);
            beenden?.();
        };
    }, [container, entwurfAusAuswahl, entwurfSetzen, geometrie, seiteVon, werkzeugAktiv]);

    // Markierungen fangen keine Maus ab (die Textauswahl darunter bleibt frei) —
    // deshalb Hover über Koordinaten statt über Ereignisse der Markierung.
    React.useEffect(() => {
        if (!container || !geometrie) return;
        const onMove = (e: PointerEvent) => {
            const ziel = e.target as HTMLElement | null;
            if (ziel && typeof ziel.closest === "function" && ziel.closest("[data-kommentar-symbol], [data-kommentar-blase]")) return;
            let gefunden: string | null = null;
            for (const t of treffer.current) {
                const el = geometrie.seitenElement(t.seite);
                if (!el) continue;
                const b = inhaltsBox(el);
                const x = e.clientX - b.left;
                const y = e.clientY - b.top;
                if (x >= t.box.left && x <= t.box.left + t.box.width && y >= t.box.top && y <= t.box.top + t.box.height) {
                    gefunden = t.id;
                    break;
                }
            }
            setHoverMarkierung((alt) => (alt === gefunden ? alt : gefunden));
        };
        const onLeave = () => setHoverMarkierung(null);
        container.addEventListener("pointermove", onMove);
        container.addEventListener("pointerleave", onLeave);
        return () => {
            container.removeEventListener("pointermove", onMove);
            container.removeEventListener("pointerleave", onLeave);
        };
    }, [container, geometrie]);

    const schliessen = React.useCallback(() => {
        setOffen(null);
        entwurfZuklappen();
    }, [entwurfZuklappen, setOffen]);

    // Klick daneben minimiert; ein Entwurf mit Text bleibt stehen (nichts Getipptes verlieren).
    React.useEffect(() => {
        if (offen === null && (!entwurf || entwurf.minimiert)) return;
        const onDown = (e: PointerEvent) => {
            const ziel = e.target as HTMLElement | null;
            if (ziel && typeof ziel.closest === "function" && ziel.closest("[data-kommentar-blase], [data-kommentar-symbol]")) {
                return;
            }
            setOffen(null);
            entwurfZuklappen();
        };
        alleDokumente.addEventListener("pointerdown", onDown, true);
        return () => alleDokumente.removeEventListener("pointerdown", onDown, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entwurf, entwurfZuklappen, offen, setOffen]);

    if (!geometrie) return null;

    // ─── Zeichnen ───────────────────────────────────────────────────────────

    type Anzeige = { faden: KommentarFaden | null; seite: number; rect: KommentarRect | null };
    const jeSeite = new Map<number, Anzeige[]>();
    const dazu = (seite: number, a: Anzeige) => jeSeite.set(seite, [...(jeSeite.get(seite) ?? []), a]);
    if (sichtbar) {
        for (const f of faeden) {
            const s = hatStelle(f, kommentare.seitenzahl) ? angezeigt(f.seite) : null;
            if (s !== null) dazu(s, { faden: f, seite: s, rect: f.rect });
            // „Ohne Stelle“: nur als offene Blase, oben auf Seite 1.
            else if (offen === f.id) dazu(0, { faden: f, seite: 0, rect: null });
        }
    }
    if (entwurf) dazu(entwurf.angezeigt, { faden: null, seite: entwurf.angezeigt, rect: entwurf.rect });
    if (zug && !jeSeite.has(zug.seite)) jeSeite.set(zug.seite, []);
    benutzteSeiten.current = new Set(jeSeite.keys());
    treffer.current = [];

    return (
        <>
            {[...jeSeite].map(([seite, liste]) => {
                const el = geometrie.seitenElement(seite);
                const v = geometrie.viewport(seite);
                if (!el || !v) return null;
                const breite = el.clientWidth || inhaltsBox(el).width;
                const host = hostFuer(seite);
                // Symbole im rechten Seitenrand (wie Acrobat) — sie verdecken keinen
                // Inhalt neben der Stelle. Vertikal mittig zur Markierung; stehen
                // mehrere auf gleicher Höhe, rücken sie untereinander.
                const sx = Math.max(breite - RAND_ABSTAND, 0);
                const boxen = new Map<string, ReturnType<typeof pdfZuCssRect> | null>();
                for (const a of liste) {
                    boxen.set(a.faden ? String(a.faden.id) : "entwurf", a.rect ? pdfZuCssRect(v, a.rect) : null);
                }
                const hoehen = new Map<string, number>();
                let unterkante = -Infinity;
                [...boxen]
                    .map(([id, b], i) => ({ id, i, y: b ? Math.max(b.top + b.height / 2 - SYMBOL / 2, 0) : 12 }))
                    .sort((a, b) => a.y - b.y || a.i - b.i)
                    .forEach(({ id, y }) => {
                        const oben = Math.max(y, unterkante + STAPEL_ABSTAND);
                        hoehen.set(id, oben);
                        unterkante = oben;
                    });
                return createPortal(
                    <>
                        {liste.map(({ faden: f }) => {
                            const id = f ? String(f.id) : "entwurf";
                            const box = boxen.get(id) ?? null;
                            const sy = hoehen.get(id) ?? 12;
                            if (box) treffer.current.push({ seite, id, box });
                            const hervor = hoverSymbol === id || hoverMarkierung === id;
                            const istOffen = f ? offen === f.id : !entwurf?.minimiert;
                            const blaseLinks =
                                sx + SYMBOL + 6 + BLASE_BREITE <= breite ? sx + SYMBOL + 6 : Math.max(sx - BLASE_BREITE - 6, 0);
                            const antworten = f?.antworten.length ?? 0;
                            return (
                                <React.Fragment key={id}>
                                    {box && (
                                        <div
                                            data-kommentar-markierung={id}
                                            data-erledigt={f?.erledigt ? "true" : "false"}
                                            data-hover={hervor ? "true" : "false"}
                                            style={{
                                                ...stil.markierung,
                                                ...(f?.erledigt ? stil.markierungErledigt : null),
                                                ...(istOffen ? stil.markierungOffen : null),
                                                ...(hervor ? stil.markierungHover : null),
                                                left: box.left,
                                                top: box.top,
                                                width: box.width,
                                                height: box.height,
                                            }}
                                        />
                                    )}
                                    {f && box && (
                                        <button
                                            type="button"
                                            data-kommentar-symbol={id}
                                            data-hover={hervor ? "true" : "false"}
                                            onPointerEnter={() => setHoverSymbol(id)}
                                            onPointerLeave={() => setHoverSymbol((h) => (h === id ? null : h))}
                                            aria-label={`Kommentar von ${f.autorName} ${istOffen ? "schließen" : "öffnen"}`}
                                            aria-expanded={istOffen}
                                            title={`${f.autorName}: ${f.text.slice(0, 80)}${f.text.length > 80 ? "…" : ""}`}
                                            style={{
                                                ...stil.symbol,
                                                ...(f.erledigt ? stil.symbolErledigt : null),
                                                ...(hervor ? stil.symbolHover : null),
                                                left: sx,
                                                top: sy,
                                            }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Ein Entwurf mit Text bleibt (minimiert) stehen — nur ein leerer weicht.
                                                if (entwurfRef.current && !istOffen && entwurfHatText()) {
                                                    onHinweis?.(ENTWURF_OFFEN);
                                                }
                                                entwurfZuklappen();
                                                setOffen(istOffen ? null : f.id);
                                            }}
                                        >
                                            <ChatRegular />
                                            {antworten > 0 && (
                                                <span data-kommentar-zaehler="" style={stil.zaehler}>
                                                    {antworten + 1}
                                                </span>
                                            )}
                                        </button>
                                    )}
                                    {!f && box && !istOffen && (
                                        <button
                                            type="button"
                                            data-kommentar-symbol="entwurf"
                                            aria-label="Kommentar-Entwurf öffnen"
                                            title="Entwurf — noch nicht gespeichert"
                                            data-hover={hervor ? "true" : "false"}
                                            onPointerEnter={() => setHoverSymbol(id)}
                                            onPointerLeave={() => setHoverSymbol((h) => (h === id ? null : h))}
                                            style={{
                                                ...stil.symbol,
                                                ...stil.symbolEntwurf,
                                                ...(hervor ? stil.symbolHover : null),
                                                left: sx,
                                                top: sy,
                                            }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOffen(null);
                                                setEntwurf((x) => (x ? { ...x, minimiert: false } : x));
                                            }}
                                        >
                                            <ChatRegular />
                                        </button>
                                    )}
                                    {istOffen && (
                                        <KommentarBlase
                                            faden={f}
                                            zitat={f ? f.zitat : (entwurf?.zitat ?? "")}
                                            position={{ left: blaseLinks, top: sy }}
                                            darfSchreiben={darfSchreiben}
                                            entwurf={textRef(id)}
                                            onSenden={async (text) => {
                                                if (f) return kommentare.antworten(f.id, text);
                                                if (!entwurf) return false;
                                                const neu = await kommentare.anlegen(
                                                    { seite: entwurf.seite, rect: entwurf.rect, zitat: entwurf.zitat },
                                                    text,
                                                );
                                                if (!neu) return false;
                                                setEntwurf(null);
                                                setOffen(neu.id);
                                                return true;
                                            }}
                                            onErledigt={(wert) => f && void kommentare.erledigtSetzen(f.id, wert)}
                                            onLoeschen={(kid) => void kommentare.loeschen(kid)}
                                            onSchliessen={schliessen}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {zug?.seite === seite && (
                            <div
                                data-kommentar-zug=""
                                style={{
                                    ...stil.zug,
                                    left: Math.min(zug.x0, zug.x1),
                                    top: Math.min(zug.y0, zug.y1),
                                    width: Math.abs(zug.x1 - zug.x0),
                                    height: Math.abs(zug.y1 - zug.y0),
                                }}
                            />
                        )}
                    </>,
                    host,
                    `kommentare-seite-${seite}`,
                );
            })}
        </>
    );
};
